import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface TeamChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  gameType: 'regular' | 'venue';
  teamSide: 'home' | 'away';
  currentTeamId: number | null;
  currentTeamName: string;
  onSuccess: () => void;
}

interface Team {
  team_id: number;
  team_name: string;
}

export const TeamChangeDialog: React.FC<TeamChangeDialogProps> = ({
  open,
  onOpenChange,
  gameId,
  gameType,
  teamSide,
  currentTeamId,
  currentTeamName,
  onSuccess
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    if (open) {
      loadTeams();
    }
  }, [open]);

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('team_id, team_name')
        .order('team_name');

      if (error) throw error;
      setTeams(data || []);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleSubmit = async () => {
    if (!changeReason.trim()) {
      toast.error('Please provide a reason for the team change');
      return;
    }

    if (selectedTeamId === currentTeamId) {
      toast.error('Please select a different team');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('change-game-team', {
        body: {
          gameId,
          gameType,
          teamSide,
          newTeamId: selectedTeamId,
          changeReason: changeReason.trim()
        }
      });

      if (error) throw error;

      toast.success(data.message || 'Team changed successfully');
      onSuccess();
      onOpenChange(false);
      
      // Reset form
      setSelectedTeamId(null);
      setChangeReason('');
    } catch (error: any) {
      console.error('Error changing team:', error);
      toast.error(error.message || 'Failed to change team');
    } finally {
      setLoading(false);
    }
  };

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change {teamSide === 'home' ? 'Home' : 'Away'} Team</DialogTitle>
          <DialogDescription>
            Replace the current team assignment. All associated data will be cleared.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Team */}
          <div>
            <Label>Current Team</Label>
            <div className="mt-1 p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">{currentTeamName}</p>
            </div>
          </div>

          {/* New Team Selection */}
          <div>
            <Label htmlFor="new-team">New Team</Label>
            <Select
              value={selectedTeamId?.toString()}
              onValueChange={(value) => setSelectedTeamId(value === 'tbd' ? null : parseInt(value))}
              disabled={loadingTeams}
            >
              <SelectTrigger id="new-team" className="mt-1">
                <SelectValue placeholder="Select new team" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="tbd">TBD (To Be Determined)</SelectItem>
                {teams
                  .filter(team => team.team_id !== currentTeamId)
                  .map((team) => (
                    <SelectItem key={team.team_id} value={team.team_id.toString()}>
                      {team.team_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Change Reason */}
          <div>
            <Label htmlFor="change-reason">Reason for Change *</Label>
            <Textarea
              id="change-reason"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Explain why this team change is necessary..."
              className="mt-1"
              rows={4}
            />
          </div>

          {/* Warning Alert */}
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This action will:
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>Remove all current team players from the game</li>
                <li>Delete all form submissions for this team</li>
                <li>Clear roster and attendance records</li>
                <li>Reset player readiness status</li>
                <li>Notify all affected team members</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Preview */}
          {selectedTeam && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">New team assignment:</p>
              <p className="text-sm mt-1">{selectedTeam.team_name}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !changeReason.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Changing Team...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Change Team
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
